import { SelectionChangedEvent } from "ag-grid-community";
import { CounterCustomerChargeEntity } from "domain/entity/CounterCustomerCharge/CounterCustomerChargeEntity";
import { CounterCustomerChargeMaintenanceConstant } from "presentation/constant/CounterCustomerCharge/CounterCustomerChargeMaintenanceConstant";
import { COUNTER_CUSTOMER_CHARGE_COL_DEF } from "presentation/constant/CounterCustomerCharge/UpdatePaidThroughDateColumnDefinition";
import { useCounterCustomerChargeMaintenanceVM } from "presentation/hook/CounterCustomerCharge/useCounterCustomerChargeMaintenanceVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { PaidThroughDateState } from "presentation/model/CounterCustomerCharge/CounterCustomerChargeMaintenanceModel";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useCounterCustomerChargeMaintenanceTracked } from "presentation/store/CounterCustomerCharge/CounterCustomerChargeMaintenanceProvider";
import { convertDateToDateTimeForComponent } from "presentation/utils/timeUtil";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import NbisDateTimePickerComponent from "presentation/view/components/NbisDateTimePickerComponent";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import NbisTable from "presentation/view/components/TableWrapper/NbisTable";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { HPHButton, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, Sidebarheader } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";
import { ChargeTypePolicy } from "constants/charge/ChargeTypePolicy";
import { VesselChargeSubChargeTypePolicy } from "constants/charge/VesselChargeSubChargeTypePolicy";
import { SubChargeTypePolicy } from "constants/charge/SubChargeTypePolicy";

const UpdatePaidThroughDatePanel = () => {
    const [counterCustomerChargeState] = useCounterCustomerChargeMaintenanceTracked();
    const counterCustomerChargeVM = useCounterCustomerChargeMaintenanceVM();
    const COUNTER_CUSTOMER_CHARGE_CONSTANT = CounterCustomerChargeMaintenanceConstant.Detail;
    const { editingEntity, paidThroughDateMap, selectedPaidThroughDateMap } = counterCustomerChargeState;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const messageBarVM = useMessageBarVM();
    const gridRef: any = useRef(null);

    const [anainfoState] = useANAInfoTracked();
    const { operatingUnit } = anainfoState;

    const vaildation = useCallback(() => {
        let isAllowUpdatePaidThroughDate = false;

        counterCustomerChargeState.selectedRows.forEach((entity) => {
            if (operatingUnit === "SAPT" || operatingUnit === "KICT") {
                if (entity.dtlState === "E" || entity.dtlState === "R" || entity.dtlState === "F" || entity.dtlState === "T" || entity.dtlState === "W") {
                    if (entity.chargeType === "ITM" || (entity.chargeType === "CC" && entity.subChargeType === "ITM")
                        || (entity.chargeType === "DEM" && entity.subChargeType === "DEI")
                        || (entity.chargeType === "RFH" && (entity.subChargeType === "PCI" || entity.subChargeType === "PCIH" || entity.subChargeType === "FDC"
                            || ((entity.cntrStatus1 === "IF" || entity.cntrStatus1 === "IM" || entity.cntrStatus1 === "TF" || entity.cntrStatus1 === "TM")
                                && (entity.subChargeType === "T2" || (entity.subChargeType === "T1" && entity.otherSplitId === null)))))
                        || ((entity.chargeType === "CSR") && (entity.subChargeType === "ST" || entity.subChargeType === "DE"))) {
                        isAllowUpdatePaidThroughDate = true;
                    }
                }
            } else {
                if ((entity.dtlState === "E" || entity.dtlState === "R" || entity.dtlState === "F" || (entity.dtlState === "T" && (operatingUnit === "OICT" || operatingUnit === "HPJZ")))) {
                    if ((operatingUnit === "AQCT" || operatingUnit === "RSCT") &&
                        (entity.adjType !== "Cancel Only" && entity.adjType !== "Cancel & Re-bill")) {
                        isAllowUpdatePaidThroughDate = true;
                    } else {
                        isAllowUpdatePaidThroughDate = true;
                    }
                }
            }
        })

        if (operatingUnit !== "SAPT" && operatingUnit !== "KICT") {
            if (!editingEntity.paidThroughDate) {
                messageBarVM.showError("Please input the Paid through date time.");
                return false;
            }
        }

        const currentDate = new Date();//dd-MM-yyyy HH:mm:ss
        if (operatingUnit === "SAPT" || operatingUnit === "KICT") {
            if (editingEntity.paidThroughDate && editingEntity.paidThroughDate < currentDate) {
                messageBarVM.showError("Paid Through Date " + convertDateToDateTimeForComponent(editingEntity.paidThroughDate) + " is not allow back date.");
                return false;
            }
        } else {
            let msg = null;
            paidThroughDateMap.forEach((value, key) => {
                if (!value) {
                    msg = "Please input the Paid Through Date.";
                    return false;
                }
                const paidDate = new Date(value);
                if (paidDate && paidDate < currentDate) {
                    if (operatingUnit === "OICT" || operatingUnit === "HPJZ") {
                        msg = "The Paid through date time should not be earlier than the current system date.";
                    } else {
                        msg = "Paid Through Date " + convertDateToDateTimeForComponent(value) + " is not allow back date.";
                    }
                    return false;
                }
            });
            if (msg) {
                messageBarVM.showError(msg);
                return false;
            }
        }

        if (!isAllowUpdatePaidThroughDate) {
            messageBarVM.showError("Just allow update the paid through date for R/F/E/T storage charge except for outbound storage.");
            return false;
        }

        return true;
    }, [editingEntity.paidThroughDate, counterCustomerChargeState.selectedRows, operatingUnit, messageBarVM, paidThroughDateMap]);

    const handleSaveClick = useCallback(async () => {
        if (!vaildation()) {
            return;
        }
        setIsLoading(true);
        try {
            const isSaptOrKict = operatingUnit === "SAPT" || operatingUnit === "KICT";
            let saveRes: { success?: boolean } | undefined;

            if (isSaptOrKict) {
                const isMatchedCharge = (item: CounterCustomerChargeEntity) => {
                    const chargeType = item.chargeType;
                    const subChargeType = item.subChargeType;
                    const cntrStatus1 = item.cntrStatus1;
                    const otherSplitId = item.otherSplitId;

                    return (
                        ChargeTypePolicy.CHARGE_TYPE_ITM === chargeType
                        || (
                            ChargeTypePolicy.CHARGE_TYPE_CC === chargeType
                            && VesselChargeSubChargeTypePolicy.CODE_ITM === subChargeType
                        )
                        || (
                            ChargeTypePolicy.CHARGE_TYPE_DEM === chargeType
                            && VesselChargeSubChargeTypePolicy.CODE_DEI === subChargeType
                        )
                        || (
                            ChargeTypePolicy.CHARGE_TYPE_RFH === chargeType
                            && (
                                SubChargeTypePolicy.CODE_PCI === subChargeType
                                || SubChargeTypePolicy.CODE_PCIH === subChargeType
                                || SubChargeTypePolicy.CODE_FDC === subChargeType
                                || (
                                    (cntrStatus1 != null && ["IF", "IM", "TF", "TM"].includes(cntrStatus1))
                                    && (
                                        SubChargeTypePolicy.CODE_T2 === subChargeType
                                        || (
                                            SubChargeTypePolicy.CODE_T1 === subChargeType
                                            && (otherSplitId === null || otherSplitId === undefined)
                                        )
                                    )
                                )
                            )
                        )
                        || (
                            ChargeTypePolicy.CHARGE_TYPE_CSR === chargeType
                            && (
                                SubChargeTypePolicy.CODE_ST === subChargeType
                                || SubChargeTypePolicy.CODE_DE === subChargeType
                            )
                        )
                    );
                };
                const entityList = counterCustomerChargeState.selectedRows.filter(isMatchedCharge).map(item => {
                    const cntrNo = item.cntrNo;

                    const paidThroughDate =
                        cntrNo && paidThroughDateMap?.has(cntrNo)
                            ? paidThroughDateMap.get(cntrNo) ?? null
                            : item.paidThroughDate;

                    return {
                        ...item,
                        paidThroughDate,
                    };
                });

                saveRes = await counterCustomerChargeVM.onSave(entityList);
            } else {
                saveRes = await counterCustomerChargeVM.updatePaidThroughDate(paidThroughDateMap);
            }

            try {
                await counterCustomerChargeVM.onSearchCounterCustomer(
                    counterCustomerChargeState.searchCriteria
                );
            } catch (e) {
                messageBarVM.showError('Save Failed.');
            }

            if (saveRes?.success) {
                messageBarVM.showSuccess("Save Successfully.");
            } else {
                messageBarVM.showError("Save Failed.");
            }

            if (isSaptOrKict) {
                if (saveRes?.success) {
                    counterCustomerChargeVM.onMoreClose();
                }
            } else {
                counterCustomerChargeVM.onMoreClose();
            }
        } catch (err) {
            messageBarVM.showError("Save Failed.");
        } finally {
            setIsLoading(false);
        }

    }, [counterCustomerChargeVM, counterCustomerChargeState.selectedRows, messageBarVM, counterCustomerChargeState.searchCriteria, vaildation, paidThroughDateMap, operatingUnit]);

    const handClose = useCallback(() => {
        counterCustomerChargeVM.onMoreClose();
    }, [counterCustomerChargeVM]);

    const allRows = useMemo<PaidThroughDateState[]>(() => {
        if (!paidThroughDateMap || paidThroughDateMap.size === 0) {
            return [];
        }
        return Array.from(paidThroughDateMap)
            .map(([containerNo, paidThroughDate]) => ({
                cntrNo: containerNo,
                paidThroughDate: paidThroughDate,
            }))
            .sort((a, b) => a.cntrNo.localeCompare(b.cntrNo));
    }, [paidThroughDateMap]);

    const handleSelectionChange = useCallback((e: SelectionChangedEvent) => {
        const selectedRows = e.api.getSelectedRows();
        counterCustomerChargeVM.updateSelectedPaidThroughDateRows(selectedRows);
    }, [counterCustomerChargeVM])

    const handApply = useCallback(() => {
        if (!selectedPaidThroughDateMap || selectedPaidThroughDateMap.size === 0) {
            messageBarVM.showError("Please select record(s).");
            return;
        }
        if (!editingEntity.paidThroughDate) {
            messageBarVM.showError("Please input the Paid through date time.");
            return;
        }
        counterCustomerChargeVM.onApplyClick(paidThroughDateMap, selectedPaidThroughDateMap);
    }, [counterCustomerChargeVM, editingEntity.paidThroughDate, messageBarVM, paidThroughDateMap, selectedPaidThroughDateMap]);

    const updatePaidThroughDatePanelTable =
        <NbisTable
            id='update-paid-through-date-table'
            isNewColumnSetting={false}
            columns={COUNTER_CUSTOMER_CHARGE_COL_DEF?.slice()}
            data={allRows ?? []}
            showPaginator={false}
            editable={false}
            showAddIcon={false}
            showUploadIcon={false}
            showDeleteButton={false}
            showReloadIcon={false}
            selectionMode={false}
            isRowHighligted={true}
            showPreferenceMenu={false}
            showSidebar={false}
            handleSelectionChanged={handleSelectionChange}
            gridHeight="customHeight"
            customHeight="calc(40vh)"
            ref={gridRef}
            rowSelection={"multiple"}
        />

    const memoPaidThroughDate = useMemo(() => {
        if (!paidThroughDateMap || paidThroughDateMap.size === 0) {
            return null;
        }

        return (
            <>
                <CriteriaItemContainer>
                    <div className="im-flex-row-item" style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
                        <NbisDateTimePickerComponent
                            disabled={false}
                            label={COUNTER_CUSTOMER_CHARGE_CONSTANT.PAID_THROUGH_DATE}
                            width="220px"
                            date={editingEntity?.paidThroughDate}
                            fieldName={"paidThroughDate"}
                            errorMessage=""
                            onDateChange={counterCustomerChargeVM.onCounterCustomerFieldChange}
                        />
                    </div>
                    <HPHButton label={'Apply'} size={'Small'} theme={'Secondary'} onClick={handApply} />
                </CriteriaItemContainer>
            </>
        );
    }, [COUNTER_CUSTOMER_CHARGE_CONSTANT.PAID_THROUGH_DATE, counterCustomerChargeVM, editingEntity?.paidThroughDate, handApply, paidThroughDateMap]);


    return (<div className='side-form-content-left-wrapper' style={{ padding: "20px 20px 20px 0px" }}>
        <div className={'flex-row'}>
            <div className={'flex-row-item flex-row-item-full-width'}>
                <Sidebarheader>
                    <HeaderTitle>{"Update Paid Through Date"}</HeaderTitle>
                </Sidebarheader>
            </div>
        </div>

        {isLoading && <Loader Indicator="Spinner" size="Medium" />}

        <div style={{ marginTop: "-50px" }}>
            <TableWrapper>
                <div style={{ marginLeft: "15px" }}>{updatePaidThroughDatePanelTable}</div>
            </TableWrapper>
        </div>

        <div className={'add-edit-form'} style={{ maxHeight: '32vh', height: '32vh', overflow: 'auto' }}>
            <div className="flex-row-auto">
                <div className={'flex-row-item'}>
                    {memoPaidThroughDate}
                </div>
            </div>
        </div>
        <SidebarActionBar>
            <HPHButton label={'Close'} size={'Small'} theme={'Secondary'} onClick={handClose} />
            <HPHButton label={'Save'} size={'Small'} theme={'Primary'} onClick={() => handleSaveClick()} />
        </SidebarActionBar>
    </div >)
}

export default memo(UpdatePaidThroughDatePanel);